import { useState } from 'react';
import Button from '@/components/Button';
import RetailerSelectBox from '@/components/RetailerSelectBox';
import { Organization } from '@/models/Organization';
import { Retailer } from '@/models/Retailer';
import { Supplier } from '@/models/Supplier';
import { CheckIcon } from '@heroicons/react/24/solid';
import { Controller, FormProvider, useForm } from 'react-hook-form';

export interface SupplierToRetailer {
  retailer?: Retailer | null;
  supplier: Supplier;
  customerCode: string;
}

interface SupplierToRetailerFormProps {
  supplier: Supplier;
  retailer?: Retailer;
  organization?: Organization;
  onSubmit: (value: SupplierToRetailer) => Promise<void>;
}

const SupplierToRetailerForm = ({
  supplier,
  retailer,
  organization,
  onSubmit,
}: SupplierToRetailerFormProps) => {
  const [loading, setLoading] = useState(false);

  const methods = useForm<SupplierToRetailer>({
    defaultValues: {
      supplier,
      retailer: retailer || null,
      customerCode: retailer?.customerCode || '',
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = methods;

  const onSubmitForm = async (data: SupplierToRetailer) => {
    setLoading(true);
    try {
      await onSubmit(data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <div className="grid grid-cols-2 gap-4 lg:gap-8">
          <div className="col-span-2">
            <label className="block font-medium text-gray-700 mb-1">Supplier:</label>
            <div className="w-full border shadow rounded-md py-2 pl-3 cursor-default">
              {supplier.name}
            </div>
          </div>

          {organization && (
            <div className="col-span-2">
              <label className="block font-medium text-gray-700 mb-1">Organization:</label>
              <div className="w-full border shadow rounded-md py-2 pl-3 cursor-default">
                {organization.name}
              </div>
            </div>
          )}

          {retailer && (
            <div className="col-span-2">
              <label className="block font-medium text-gray-700 mb-1">Retailer:</label>
              <div className="w-full border shadow rounded-md py-2 pl-3 cursor-default">
                {retailer.name}
              </div>
            </div>
          )}

          {!retailer && (
            <div className="col-span-2">
              <Controller
                name="retailer"
                control={control}
                rules={{ required: 'Retailer is required' }}
                render={({ field }) => (
                  <RetailerSelectBox
                    value={field.value || null}
                    onSelect={(selected) => field.onChange(selected || null)}
                    showOrganization
                  />
                )}
              />
              {errors.retailer && (
                <span className="text-red-500 text-sm">{errors.retailer.message}</span>
              )}
            </div>
          )}

          <div className="col-span-2">
            <label htmlFor="customerCode" className="block font-medium text-gray-700">
              Customer Number:
            </label>
            <Controller
              name="customerCode"
              control={control}
              rules={{ required: 'Retailer code is required' }}
              render={({ field }) => (
                <input
                  type="text"
                  id="customerCode"
                  {...field}
                  className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                />
              )}
            />
            {errors.customerCode && (
              <p className="text-red-500 text-sm">{errors.customerCode.message}</p>
            )}
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <Button
            type="submit"
            icon={<CheckIcon className="h-6 w-6" aria-hidden="true" />}
            loadingIconClassName="h-5 w-5 mr-1"
            variant="primary"
            loading={loading}
          >
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default SupplierToRetailerForm;
